import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Upload, FolderOpen, FileStack, Settings as SettingsIcon, Sun, Moon, LogOut, Sparkles, ChevronDown, User, Wand2, Users as UsersIcon, Briefcase, Target, Send, Mail, BarChart3, Building2, FileText, Calendar, Crown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import GlobalCopilotDrawer from "@/components/GlobalCopilotDrawer";

const NAV_BASE = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/upload", icon: Upload, label: "Upload", testid: "nav-upload" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [appName, setAppName] = useState("ResumeForge");
  const [featureFlags, setFeatureFlags] = useState({});
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    api.get("/settings/app").then((r) => {
      const s = r.data || {};
      setAppName(s.app_name || "ResumeForge");
      // Apply favicon + document title from settings
      if (s.app_name) document.title = s.app_name;
      if (s.feature_flags) setFeatureFlags(s.feature_flags);
      const faviconUrl = s.favicon_url ? (s.favicon_url.startsWith("http") ? s.favicon_url : `${BACKEND_URL}${s.favicon_url}`) : null;
      if (faviconUrl) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          document.head.appendChild(link);
        }
        link.href = faviconUrl;
      }
    }).catch(() => {});
    api.get("/plans/me/quota").then((r) => setQuota(r.data)).catch(() => {});
  }, []);

  const NAV = [
    { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard", key: "dashboard" },
    { to: "/upload", icon: Upload, label: "Upload", testid: "nav-upload", key: "upload" },
    { to: "/builder", icon: Wand2, label: "AI Builder", testid: "nav-builder", key: "builder" },
    { to: "/organizer", icon: FolderOpen, label: "Candidates", testid: "nav-organizer", key: "organizer" },
    { to: "/job-profiles", icon: Briefcase, label: "Job Profiles", testid: "nav-job-profiles", key: "job_profiles" },
    { to: "/clients", icon: Building2, label: "Clients", testid: "nav-clients", key: "clients" },
    { to: "/submissions", icon: Send, label: "Submissions", testid: "nav-submissions", key: "submissions" },
    { to: "/email-center", icon: Mail, label: "Email Center", testid: "nav-email-center", key: "email_center" },
    { to: "/interviews", icon: Calendar, label: "Interviews", testid: "nav-interviews", key: "interviews" },
    { to: "/documents", icon: FileText, label: "HR Documents", testid: "nav-documents", key: "documents", adminOnly: true },
    { to: "/reports", icon: BarChart3, label: "Reports", testid: "nav-reports", key: "reports" },
    { to: "/templates", icon: FileStack, label: "Templates", testid: "nav-templates", key: "templates" },
    ...(["super_admin", "company_admin"].includes(user?.role) ? [{ to: "/users", icon: UsersIcon, label: "Users", testid: "nav-users", key: "users" }] : []),
    ...(["super_admin", "company_admin"].includes(user?.role) ? [{ to: "/companies", icon: Building2, label: "Companies", testid: "nav-companies", key: "companies" }] : []),
    { to: "/plans", icon: Crown, label: "Plans", testid: "nav-plans", key: "plans" },
    { to: "/settings", icon: SettingsIcon, label: "Settings", testid: "nav-settings", key: "settings" },
  ].filter((n) => {
    // Admin-only items: hide from recruiter/data_entry regardless of flag
    if (n.adminOnly && !["super_admin", "company_admin"].includes(user?.role)) return false;
    // Feature flag: hide if explicitly disabled
    if (n.key && featureFlags[n.key] === false) return false;
    return true;
  });

  const initials = (user?.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-border bg-card sticky top-0 h-screen">
        <div className="px-5 pt-6 pb-4 flex items-center gap-2">
          <div className="size-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
            <Sparkles className="size-4" strokeWidth={2.4} />
          </div>
          <div>
            <div className="display text-lg font-extrabold tracking-tight leading-none">{appName}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">AI Standardizer</div>
          </div>
        </div>

        <nav className="px-3 py-2 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground px-3 mb-2 mt-2">Workspace</div>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium lift ${
                  isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <n.icon className="size-4" strokeWidth={1.6} />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-border space-y-2">
          {quota && (
            <button onClick={() => navigate("/plans")} className="w-full text-left rounded-lg border border-border p-2.5 lift hover:border-primary/50 bg-card" data-testid="sidebar-quota">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="size-2 rounded-full shrink-0" style={{ background: quota?.plan?.color || "#1E5BFF" }} />
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground truncate">{quota?.plan?.name || "Free"}</span>
                </div>
                <Crown className="size-3 text-muted-foreground shrink-0" />
              </div>
              {quota?.company?.name && (
                <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground/80 font-bold truncate mb-1 flex items-center gap-1">
                  <Building2 className="size-2.5" /> {quota.company.name}
                </div>
              )}
              <div className="flex items-baseline justify-between">
                <div className="display font-bold text-sm">
                  {quota.used}
                  {quota.max > 0 ? <span className="text-muted-foreground text-xs"> / {quota.max}</span> : <span className="text-emerald-600 text-[10px] ml-1 uppercase tracking-wider font-bold">Unlimited</span>}
                </div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">used</div>
              </div>
              {quota.max > 0 && (
                <div className="mt-1.5 h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (quota.used / quota.max) * 100)}%`, background: quota.over_limit ? "#EF4444" : (quota?.plan?.color || "#1E5BFF") }} />
                </div>
              )}
              {quota.over_limit && <div className="text-[10px] text-destructive font-semibold mt-1">Quota exceeded — archive or upgrade.</div>}
            </button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 lift"
            onClick={toggle}
            data-testid="theme-toggle-button"
          >
            {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-background/80 backdrop-blur sticky top-0 z-20 flex items-center justify-between px-4 md:px-6">
          <div className="md:hidden flex items-center gap-2">
            <div className="size-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Sparkles className="size-3.5" />
            </div>
            <span className="font-extrabold display">{appName}</span>
          </div>
          <div className="hidden md:block text-xs uppercase tracking-[0.2em] text-muted-foreground">Premium SaaS · v1.0</div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} className="md:hidden" data-testid="theme-toggle-mobile">
              {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-secondary lift">
                  <Avatar className="size-7">
                    <AvatarImage src={user?.avatar_url ? `${BACKEND_URL}${user.avatar_url}` : undefined} alt={user?.full_name || "User"} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left">
                    <div className="text-xs font-semibold leading-none">{user?.full_name}</div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mt-0.5">{(user?.role || "").replace("_", " ")}</div>
                  </div>
                  <ChevronDown className="size-3.5 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <User className="size-4 mr-2" /> Profile & Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggle} data-testid="menu-theme">
                  {theme === "dark" ? <Sun className="size-4 mr-2" /> : <Moon className="size-4 mr-2" />}
                  {theme === "dark" ? "Light mode" : "Dark mode"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="menu-logout">
                  <LogOut className="size-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 min-w-0">{children}</main>
      </div>

      {/* Global AI Copilot — floating launcher + side drawer.  Available
          across the whole authenticated app for workspace-level Q&A,
          drafting, and feature guidance. */}
      <GlobalCopilotDrawer />
    </div>
  );
}
